import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue} from "@cm/graph-v3"
import {parameterValue} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type BooleanOperationType = "and" | "or" | "not"

export type BooleanOperationParameters = {
    operator: BooleanOperationType
    inputs: ParameterValue<boolean, PricingContext>[] //"not" takes exactly one input
}

const booleanOperationDefinition = {
    nodeClass: "BooleanOperation",
    validation: {
        parameters: z.object({
            operator: z.union([z.literal("and"), z.literal("or"), z.literal("not")]),
            inputs: z.array(parameterValue(z.boolean())),
        }),
        returns: z.boolean(),
    },
} satisfies GraphNodeDefinition<boolean, BooleanOperationParameters>

/** Combines boolean inputs (conditions or other BooleanOperations). See Conditions.md. */
@registerPricingGraphNode
export class BooleanOperation extends PricingNode<boolean, BooleanOperationParameters> {
    public override get definition() {
        return booleanOperationDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        const {operator, inputs} = this.parameters
        //an empty "and" would silently be true, so fail loudly instead
        if (inputs.length === 0) throw new Error(`BooleanOperation: "${operator}" without inputs`)
        if (operator === "not" && inputs.length !== 1) throw new Error(`BooleanOperation: "not" takes exactly one input, got ${inputs.length}`)

        const values = await Promise.all(inputs.map((input) => evaluator.get(input)))
        switch (operator) {
            case "and":
                return values.every((value) => value)
            case "or":
                return values.some((value) => value)
            case "not":
                return !values[0]
        }
    }
}
